import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { fetchArticles } from '../lib/cms';
import InitialConsultationCallout from '../components/InitialConsultationCallout';

export default function BlogIndexPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const data = await fetchArticles();
      setArticles(data || []);
      setLoading(false);
    }
    load();
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title="Blogindlæg :: KerneViden"
        description="Blogindlæg og faglige perspektiver på nervesystemet, adfærd, relationer og autisme."
        ogTitle="Blogindlæg :: KerneViden"
        ogDescription="Blogindlæg og faglige perspektiver på nervesystemet, adfærd, relationer og autisme."
        ogImage="https://assets.auxodev.dk/assets/1d30bca3-a748-4ede-8335-828014d68a29/f31895a0-283e-4feb-8720-c86667d599ad_5c85b2c3.png"
      />

      {/* Hero Header */}
      <section className="pt-12 pb-16 bg-white border-b border-[#EAE2D7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#233424] font-normal tracking-tight">
            Blogindlæg.
          </h1>
          <p className="font-serif text-lg sm:text-xl text-[#526051] italic max-w-2xl mx-auto">
            Perspektiver på nervesystemet, adfærd, relationer og autisme.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid Section */}
      <section className="py-14 sm:py-20 bg-white" aria-label="Artikler og indlæg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16 space-y-3">
              <i className="fa-solid fa-circle-notch fa-spin text-3xl text-[#3A4F3D]" aria-hidden="true" />
              <p className="text-sm text-[#6A7869]">Henter artikler...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {articles.map((article, index) => {
                const slug = article.custom_slug || article.id;
                const imgSrc = article.image?.[0];

                return (
                  <article
                    key={article.id || slug || index}
                    className="bg-white rounded-3xl overflow-hidden shadow-[0_16px_45px_rgba(44,62,45,0.06)] hover:shadow-[0_22px_55px_rgba(44,62,45,0.12)] transition-all duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      {imgSrc && (
                        <div className="relative aspect-[16/10] overflow-hidden bg-[#EFEAE2]">
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${imgSrc.replace('https://assets.auxodev.dk/', '')}`}
                            alt={article.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      )}

                      <div className="p-6 sm:p-7 space-y-3">
                        <time className="text-xs font-semibold uppercase tracking-wider text-[#8A9887] block">
                          {article.dateFormatted || article.date}
                        </time>

                        <h2 className="font-serif text-xl sm:text-2xl text-[#233424] font-medium tracking-tight group-hover:text-[#3A4F3D] transition-colors leading-snug">
                          <Link to={`/l/${slug}`}>
                            {article.title}
                          </Link>
                        </h2>

                        {article.summary && (
                          <p className="text-sm leading-relaxed text-[#556354] line-clamp-3">
                            {article.summary}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="px-6 pb-6 sm:px-7 sm:pb-7 pt-2">
                      <Link
                        to={`/l/${slug}`}
                        className="inline-flex items-center text-sm font-semibold text-[#233424] group-hover:text-[#3A4F3D] transition-colors"
                      >
                        <span>Læs hele indlægget</span>
                        <i className="fa-solid fa-arrow-right ml-2 text-xs transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Consultation Callout */}
      <InitialConsultationCallout
        title="Har du spørgsmål til et emne eller forløb?"
        description="Du er altid velkommen til at række ud til en gratis og uforpligtende samtale."
        ctaText="Tag kontakt i dag"
      />
    </div>
  );
}
